import { CredentialResponse, GoogleOAuthProvider } from '@react-oauth/google';
import './microsoft.css';
import React, { Dispatch } from 'react'
import { GoogleLogin, GoogleLoginProps } from '@react-oauth/google';
import { compareVersions, logInfo } from 'core';
import { getServicesManager } from 'services';
import { CHROME_EXTENSION_ACTIVATE_PAGE, CHROME_EXTENSION_START_PAGE, EXTENSION_ID, MESSAGE_TYPES, MINIMUM_EXTENSION_VERSION_FOR_NEW_LOGIN_FLOW, OAUTHLOGIN_TYPES, SERVICES_HOSTNAME } from 'cfg/endpoints';
import { Alert, AlertProps, AlertType } from 'components/Alert';
import { getWidth } from 'lib/user-agent';
import { TrackerPayloadParam } from "interfaces/auth";
import { Typography } from 'interfaces/typography';
import { Configuration, PublicClientApplication, OIDC_DEFAULT_SCOPES } from "@azure/msal-browser";
import { MicrosoftLoginButton } from './MSFTButton';
import { connect } from 'react-redux';
import { RootState } from 'store';
import { AnyAction } from '@reduxjs/toolkit';
import { updateAuthorizationInfo } from 'lib/redux/users/slice';
import { Navigate } from 'react-router-dom';
import { TRACKER_TYPE } from '../../cfg/sources';

// MSAL configuration
const configuration: Configuration = {
    auth: {
        clientId: "7a586899-6432-4680-99f4-122a845a4f24",
        authority: "https://login.microsoftonline.com/common",
        // When using loginPopup(), all the popup needs to do is pass the token back to the page that opened it
        // (namely this one), so we really don't care what page loads in the popup once it has the token.
        // Therefore redirectUri can be anything lightweight---the docs even recommend a blank page---
        //     https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/FAQ.md#redirecturi-for-popup-and-silent-flows
        // and we don't need to navigateToLoginRequestUrl either.
        redirectUri: `${window.location.origin}/login`,
        navigateToLoginRequestUrl: false,
    }
};
const pca = new PublicClientApplication(configuration);
pca.initialize()
// Function to sign in

export enum AuthState {
    SIGNIN = 'SIGNIN',
    SIGNUP = 'SIGNUP',
    PASSWORD_RESET_REQUEST = 'PASSWORD_RESET_REQUEST',
    PASSWORD_RESET = 'PASSWORD_RESET'
}

export type LoginState = {
    authState: AuthState
    showAlert: boolean
    alertProps?: AlertProps
    
    name?: string,
    email?: string,
    teamId?: string,
    password?: string,
    recovery_code?: string,
    emailFromUrl?: string,

    extensionId?: string,
    extensionVersion?: number[],
    shouldRedirect?: boolean,
    redirectTo?: string
}

const ClaraCallLogo = <svg width="130" height="96" viewBox="0 0 65 96" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M26.4965 18.0458C27.3425 18.6492 28.0345 19.9861 28.0345 21.0273V46.0389C28.0345 47.0741 27.3425 47.435 26.4965 46.8375L1.544 29.2265C0.697999 28.629 0.00589752 27.292 0.00589752 26.2509V1.08547C0.00589752 0.0502189 0.697999 -0.310639 1.544 0.292763L26.5025 18.0458H26.4965ZM46.8045 35.6746C45.9595 35.0712 44.5805 35.0712 43.7345 35.6746L1.5321 65.8624C0.686204 66.4658 0 67.8087 0 68.8439V94.3051C0 95.3403 0.692105 95.6953 1.5321 95.0919L64.1265 50.2154C64.9665 49.612 64.9665 48.624 64.1265 48.0206L46.8105 35.6687L46.8045 35.6746Z" fill="url(#paint0_linear_3836_7223)"/>
<defs>
<linearGradient id="paint0_linear_3836_7223" x1="0" y1="47.6953" x2="64.7595" y2="47.6953" gradientUnits="userSpaceOnUse">
<stop stopColor="#0C93E0"/>
<stop offset="0.45" stopColor="#18BDD6"/>
<stop offset="0.79" stopColor="#20D8D0"/>
<stop offset="1" stopColor="#23E2CE"/>
</linearGradient>
</defs>
</svg>

const _LOG_SCOPE = '[ClaraCall][External signin]'

type LoginProps = {
    viewState?: AuthState
    viaMainPage?: boolean
    dispatch: Dispatch<AnyAction>
}


class AuthenticationImpl extends React.Component<LoginProps, LoginState> {
    constructor(props: LoginProps) {
        const urlParams = new URLSearchParams(window.location.search)
        const email = urlParams.get('email')
        const recovery_code = urlParams.get('recovery_code')
        const teamId = urlParams.get('team_id')
        super(props)
        this.state = {
            'authState': props.viewState ?? (email ? AuthState.SIGNUP: AuthState.SIGNIN),
            'showAlert': false,
            'email': email ?? undefined,
            'recovery_code': recovery_code ?? undefined,
            'emailFromUrl': email ?? undefined,
            'teamId': teamId ?? undefined,
        }
    }

    _onExtensionMessageBounded = this._onExtensionMessage.bind(this)

    componentDidMount(): void {
        logInfo('registering event listener')
        window.addEventListener('message', this._onExtensionMessageBounded)
      }

    componentWillUnmount(): void {
        logInfo('unregistering event listener')
        window.removeEventListener('message', this._onExtensionMessageBounded)
    }

    _onExtensionMessage (event: any) {
        const message = event.data
        logInfo(`${_LOG_SCOPE} Receiving message type ${message['type']}`)
        switch (message['type']) {
          case MESSAGE_TYPES.APP_TO_EXTERNAL_SET_EXTENSION_INFO:
            const extensionId = message['extensionId']
            if (message['extensionVersion']) {
                const extensionVersion: number[] = message['extensionVersion'].split('.').map((x: string) => {return parseInt(x)})
                logInfo(`${_LOG_SCOPE} Extension version is ${extensionVersion}`)
                this.setState({'extensionVersion': extensionVersion})
            }
            this.setState({'extensionId': extensionId})
            break
          case MESSAGE_TYPES.APP_TO_EXTERNAL_CHECK_IS_LOADED:
            window.postMessage({'type': MESSAGE_TYPES.EXTERNAL_TO_APP_IS_LOADED})
            break
          default:
            logInfo(`${_LOG_SCOPE} Skipping message of type ${message['type']}`)
            break
        }
    }

    /**
     * Sign up the user and store the resulting api key in local storage
     */
    async _passwordSignInUser () {
        // make the request
        if (!this.state.email || !this.state.password){
            this._addAlert('Please fill out all fields', false)
            return
        }

        // make the request
        logInfo(`${_LOG_SCOPE} Forming signin user request`)
        const authResult = await getServicesManager().passwordLogin(this.state.email, this.state.password)
        if (authResult == null) {
            logInfo(`${_LOG_SCOPE} Invalid user signin response`)
            this._addAlert('Invalid credentials...please try again', false) 
        } else {
            logInfo(`${_LOG_SCOPE} Got valid auth response from password signin`)
            this._setApiKey.bind(this)(authResult.api_key)
        }
    }

    /**
     * Sign up the user and store the resulting api key in local storage
     */
    async _passwordSignupUser () {
        // make the request
        if (!this.state.email || !this.state.name || !this.state.password){
            this._addAlert('Please fill out all fields', false)
            return
        } 
        // make the request
        logInfo(`${_LOG_SCOPE} Forming signup user request`)
        const authResult = await getServicesManager().passwordSignup(this.state.name, this.state.email, this.state.password, this.state.teamId)
        if (authResult == null) {
            logInfo(`${_LOG_SCOPE} Invalid user signup response`)
            this._addAlert('Error signing up...please try again', false) 
        } else {
            logInfo(`${_LOG_SCOPE} Got valid user signup response from google oauth`)
            this._setApiKey.bind(this)(authResult.api_key)
        }
  }

  async _resetPasswordUser() {
    if (!this.state.email || !this.state.password || !this.state.recovery_code) {
        this._addAlert('Please ensure all fields/url are correct')
        return
    }

    // make the request
    logInfo(`${_LOG_SCOPE} Forming password request`)    
    const authResult = await getServicesManager().resetPassword(this.state.email, this.state.recovery_code, this.state.password)
    if (authResult == null) {
        logInfo(`${_LOG_SCOPE} Invalid password reset`)
        this._addAlert('Error resetting password...please try again', false) 
    } else {
        logInfo(`${_LOG_SCOPE} Got valid user signup response from google oauth`)
        this._setApiKey.bind(this)(authResult.api_key)
    }
  }

  async update_local_storage(apiKey: string) {
        let hasChrome = false
        try { hasChrome = chrome != null && chrome.runtime != null
        } catch (e) { }
        const api_info = {'apiKey': apiKey, 'services': SERVICES_HOSTNAME, 'has_extension': hasChrome}
        getServicesManager().updateApiInfo(api_info)
        this.props.dispatch(updateAuthorizationInfo({'value': {'apiKey': apiKey, 'services': SERVICES_HOSTNAME, 'has_extension': hasChrome}}))
        // Wait a bit to ensure the Redux store is updated
        await new Promise(resolve => setTimeout(resolve, 200))
        return true
  }

  _constructStartPage(extensionId: string) {
    return 'chrome-extension://' + extensionId + '/start_page.html'
  }

  _constructDialerActivation(extensionId: string) {
    return 'chrome-extension://' + extensionId + '/pages/activate_dialers.html'
  }

  _redirectBasedOfPageWidth() {
    if (this.props.viaMainPage) {
        let returnTo = new URLSearchParams(window.location.search).get('returnTo') ?? '/'
        // censor out URLs that aren't whole path+search constructions
        // like /login?returnTo=https%3a%2f%2fsome.phishing%2fpage .
        if (
            !returnTo.startsWith('/')  // reject relative paths
            || returnTo.startsWith('//')  // reject protocol-relative URLs
        ) {
            returnTo = '/'
        }
        this.setState({ shouldRedirect: true, redirectTo: returnTo })
        return
    }
    window.location.replace(this.state.extensionId ? this._constructDialerActivation(this.state.extensionId) : CHROME_EXTENSION_ACTIVATE_PAGE)
    }

    _closeAlert() {
        this.setState({'showAlert': false, 'alertProps': undefined})
    }

    _addAlert(label: string, isSuccess?: boolean, time?: number) {
        this.setState({'showAlert': true, alertProps: {'alertType': AlertType.STANDARD, 'bgColorTailwind': isSuccess ? 'bg-green-800' : 'bg-red-800',  onCloseOrNo: this._closeAlert.bind(this), stringToShow: label}}, 
        () => setTimeout(() => this._closeAlert.bind(this)(), time ?? 5000))
    }

    _setApiKey(apiKey: string) {
        const submitLoginTrackerInformation = () => {
            const baseUrl = window.location.hostname
            getServicesManager().submitTrackers(baseUrl, TRACKER_TYPE.INSTALL, undefined, apiKey)
        }
        submitLoginTrackerInformation()
        this.update_local_storage(apiKey).then(() => this._redirectBasedOfPageWidth())
    }
  
    async _handleMSFTLogin() {
        let idToken = null
        try {
            const loginResponse = await pca.loginPopup({
                scopes: OIDC_DEFAULT_SCOPES,
                loginHint: this.state.email?.trim() || undefined,  // email address if we're locking it in
            });
            idToken = loginResponse && loginResponse.idToken ? loginResponse.idToken : idToken
          } catch (error) {
        }
        // make the request
        logInfo(`${_LOG_SCOPE} Forming signup user request with oauth`)
        if (!idToken) {
            this._addAlert('Error logging in with microsoft...please try again', false) 
            return
        }
        const authResult = await getServicesManager().oauthLogin(idToken, OAUTHLOGIN_TYPES.MICROSOFT_LOGIN, this.state.teamId)
        if (authResult == null) {
            logInfo(`${_LOG_SCOPE} Invalid user signup response`)
            this._addAlert('Error logging in with microsoft...please try again', false) 
        } else {
            logInfo(`${_LOG_SCOPE} Got valid user signup response from microsoft oauth`)
            this._setApiKey.bind(this)(authResult.api_key)
        }
    }   

    async _handleGoogleCredentialResponse(credential: CredentialResponse) {
        // make the request
        logInfo(`${_LOG_SCOPE} Forming signup user request with oauth`)
        if (!credential.credential) {
            this._addAlert('Error logging in with google...please try again', false) 
            return
        }
        const authResult = await getServicesManager().oauthLogin(credential.credential, OAUTHLOGIN_TYPES.GOOGLE_LOGIN, this.state.teamId)
        if (authResult == null) {
            logInfo(`${_LOG_SCOPE} Invalid user signup response`)
            this._addAlert('Error logging in with google...please try again', false) 
        } else {
            logInfo(`${_LOG_SCOPE} Got valid user signup response from google oauth`)
            this._setApiKey.bind(this)(authResult.api_key)
        }
    }


    _renderCTAButton(call_to_action: string, onClick: () => {}): JSX.Element {
        return <button 
            onClick={onClick.bind(this)} 
            className='w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
            style={{
                transition: 'all 0.15s ease-in-out',
                transform: 'translateY(0px)',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0px)';
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            {call_to_action}
        </button>
    }

  _renderSigninForm(): JSX.Element {
    const inputStyle = {
        transition: 'all 0.15s ease-in-out',
        boxShadow: 'none',
    };
    
    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)';
        e.currentTarget.style.borderColor = '#10b981';
    };
    
    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = '#d1d5db';
    };

    return (
        <div className="space-y-4">
            <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email address
                </label>
                <input 
                    id="email"
                    className='w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none' 
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onChange={(e) => this.setState({'email': e.target.value})} 
                    value={this.state.email ?? ''} 
                    type={'email'} 
                    placeholder={'Enter your email'} 
                />
            </div>
            <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Password
                </label>
                <input 
                    id="password"
                    className='w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none' 
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onChange={(e) => this.setState({'password': e.target.value})} 
                    value={this.state.password ?? ''} 
                    type={'password'} 
                    placeholder={'Enter your password'} 
                />
            </div>
            {this._renderCTAButton('Sign in', this._passwordSignInUser.bind(this))}
        </div>)
  }

  _renderSignupForm(): JSX.Element {
    const inputStyle = {
        transition: 'all 0.15s ease-in-out',
        boxShadow: 'none',
    };
    
    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)';
        e.currentTarget.style.borderColor = '#10b981';
    };
    
    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = '#d1d5db';
    };

    return (
    <div className="space-y-4">
        <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                Full name
            </label>
            <input 
                id="name"
                className='w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none' 
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onChange={(e) => this.setState({'name': e.target.value})} 
                value={this.state.name ?? ''}  
                type={'text'} 
                placeholder={'Enter your full name'} 
            />
        </div>
        <div>
            <label htmlFor="email-signup" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email address
            </label>
            <input 
                id="email-signup"
                className='w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none' 
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onChange={(e) => this.setState({'email': e.target.value})} 
                value={this.state.email ?? ''}  
                type={'email'} 
                placeholder={'Enter your email'} 
            />
        </div>
        <div>
            <label htmlFor="password-signup" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
            </label>
            <input 
                id="password-signup"
                className='w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none' 
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onChange={(e) => this.setState({'password': e.target.value})} 
                value={this.state.password ?? ''} 
                type={'password'} 
                placeholder={'Create a password'} 
            />
        </div>
        {this._renderCTAButton('Create account', this._passwordSignupUser.bind(this))}
    </div>)
  }

  _renderPasswordResetForm(): JSX.Element {
    const inputStyle = {
        transition: 'all 0.15s ease-in-out',
        boxShadow: 'none',
    };
    
    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)';
        e.currentTarget.style.borderColor = '#10b981';
    };
    
    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = '#d1d5db';
    };

    return (
        <div className="space-y-4">
            <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1.5">
                    New password
                </label>
                <input 
                    id="new-password"
                    className='w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none' 
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onChange={(e) => this.setState({'password': e.target.value})} 
                    value={this.state.password ?? ''} 
                    type={'password'} 
                    placeholder={'Enter your new password'} 
                />
            </div>
            {this._renderCTAButton('Reset password', this._resetPasswordUser.bind(this))}
        </div>)
    }

  _renderInputForm(): JSX.Element | null{
    return (
        this.state.authState === AuthState.SIGNIN ? this._renderSigninForm() : this.state.authState === AuthState.SIGNUP ? this._renderSignupForm() : this.state.authState === AuthState.PASSWORD_RESET ? this._renderPasswordResetForm() : null
    )
  }

  _renderBottomCTA(): JSX.Element {
    return (
    <div className='flex flex-row gap-1 items-center justify-center text-sm text-gray-600'>
        <span id="sign-in-type-text">{this.state.authState === AuthState.SIGNIN ? 'Don\'t have an account?' : 'Already have an account?'}</span> 
        <button 
            id="sign-in-type-button" 
            onClick={() => this.setState((state) => {return {'authState': state.authState === AuthState.SIGNIN ? AuthState.SIGNUP : AuthState.SIGNIN, 'email': undefined, 'name': undefined, 'password': undefined}})} 
            className="font-medium text-cyan-600 hover:text-cyan-700 focus:outline-none focus:underline"
            style={{ transition: 'color 0.15s ease-in-out' }}
        >
            {this.state.authState === AuthState.SIGNIN ? 'Sign up' : 'Sign in'}
        </button>
    </div>)
  }

  _renderPasswordResetCTA(): JSX.Element | null {
    if (this.state.authState !== AuthState.SIGNIN) return null
    return (
    <div className='flex flex-row gap-1 items-center justify-center text-sm text-gray-600'>
        <span id="sign-in-type-text">{"Forgot your password?"}</span> 
        <button 
            id="sign-in-type-button" 
            onClick={() => this.setState((state) => {return {'authState': AuthState.PASSWORD_RESET_REQUEST, 'email': undefined}})}
            className="font-medium text-cyan-600 hover:text-cyan-700 focus:outline-none focus:underline"
            style={{ transition: 'color 0.15s ease-in-out' }}
        >
            {'Reset it here'}
        </button>
    </div>)
  }


  _renderTermsAndService(): JSX.Element {
    return (
        <div className='w-full flex flex-row items-center justify-center gap-1 text-xs text-gray-500'>
            <Typography variant='caption' color='gray-500'>
                By continuing, you agree to our 
            </Typography>
            <a
                href='https://claracall.com/terms-of-service/'
                target={'_blank'}
                rel="noreferrer"
                className="font-medium text-cyan-600 hover:text-cyan-700"
                style={{ transition: 'color 0.15s ease-in-out' }}
            >
                Terms of Service
            </a>
        </div>
    )
  }

  renderLoginOrSignupPage(): JSX.Element | null {
    if (![AuthState.SIGNIN, AuthState.SIGNUP].includes(this.state.authState)) return null
    const googleLoginProps: GoogleLoginProps = {
        onSuccess: (credentialResponse: CredentialResponse) => this._handleGoogleCredentialResponse.bind(this)(credentialResponse),
        onError: () => this._addAlert.bind(this)('Google login failed', false),
    }
    // gsi supports IdConfiguration.login_hint
    //   https://developers.google.com/identity/gsi/web/reference/js-reference#IdConfiguration
    // and @react-oauth/google doesn't have it yet in v0.12.2
    //   https://github.com/MomenSherif/react-oauth?tab=readme-ov-file#googlelogin
    // but @react-oauth/google just passes it blindly through to gsi so just appease the typechecker
    const googleLoginPropsTrustMeBro = {
        ...googleLoginProps,
        login_hint: this.state.email?.trim() || undefined,
    } as GoogleLoginProps
    return (
        <div className='space-y-6'>
            {/* OAuth Buttons */}
            <div className="space-y-3">
                <div className="flex justify-center">  
                    <GoogleLogin {...googleLoginPropsTrustMeBro} />
                </div>
                <MicrosoftLoginButton onClick={this._handleMSFTLogin.bind(this)}/>
            </div>
            
            {/* Divider */}
            <div className="relative">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">or continue with email</span>
                </div>
            </div>

            {/* Email Form */}
            {this._renderInputForm()}
            
            {/* Bottom Links */}
            <div className="space-y-4 text-center">
                {this._renderBottomCTA()}
                {this._renderPasswordResetCTA()}
            </div>
            
            {/* Terms */}
            <div className="pt-4 border-t border-gray-100">
                {this._renderTermsAndService()}
            </div>
        </div>
    )
  }

  async requestPasswordRestEmail() {
    if (!this.state.email) return null
    const result = await getServicesManager().requestPasswordResetEmail(this.state.email)
    this._addAlert(result ? 'Check your email for a password reset email' : 'No user with that email address found - try again', result, 8000)
  }

  renderPasswordResetRequest(): JSX.Element | null {
    if (this.state.authState !== AuthState.PASSWORD_RESET_REQUEST) return null
    return <div className='space-y-6'>
            <div className="space-y-4">
                <div>
                    <label htmlFor="email-reset" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Email address
                    </label>
                    <input 
                        id="email-reset"
                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-500' 
                        onChange={(e) => this.setState({'email': e.target.value})} 
                        value={this.state.email ?? ''}  
                        type={'email'} 
                        placeholder={'Enter your email address'} 
                        contentEditable={true} 
                    />
                </div>
                {this._renderCTAButton('Send reset link', this.requestPasswordRestEmail.bind(this))}
            </div>

            <div className='flex flex-row gap-1 items-center justify-center text-sm text-gray-600'>
                <span id="sign-in-type-text">{"Remember your password?"}</span> 
                <button 
                    id="sign-in-type-button" 
                    onClick={() => this.setState((state) => {return {'authState': AuthState.SIGNIN}})} 
                    className="font-medium text-green-600 hover:text-green-700 transition-colors duration-150 focus:outline-none focus:underline"
                >
                    {'Back to sign in'}
                </button>
            </div>
    </div>
  }

  renderPasswordReset(): JSX.Element | null {
    if (![AuthState.PASSWORD_RESET].includes(this.state.authState)) return null
    return (
        <div className='space-y-6'>
            {this._renderInputForm()}
            
            <div className="space-y-4 text-center">
                {this._renderBottomCTA()}
                {this._renderPasswordResetCTA()}
            </div>
            
            <div className="pt-4 border-t border-gray-100">
                {this._renderTermsAndService()}
            </div>
        </div>
    )
  }

  renderInvitationBanner(): JSX.Element | null {
    if (!this.state.emailFromUrl) return null
    
    return (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mx-8 mb-4">
            <div className="flex items-center">
                <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                </div>
                <div className="ml-3">
                    <Typography variant="mediumParagraph" color="#1e40af" fontWeight={600}>
                        Team Invitation
                    </Typography>
                    <Typography variant="smallParagraph" color="#374151" style={{ marginTop: '4px' }}>
                        You have been invited to join a team. Sign in with Google/Microsoft or create an account with email and password using{' '}
                        <strong>{this.state.emailFromUrl}</strong> to access your team invitation.
                    </Typography>
                </div>
            </div>
        </div>
    )
  }

  render(): JSX.Element {
    if (this.state.shouldRedirect && this.state.redirectTo) {
        return <Navigate to={this.state.redirectTo} replace />
    }
    
    return (
    <GoogleOAuthProvider clientId='50226627522-ekkero1qdgs8glk3t809ice0ld8h4i2n.apps.googleusercontent.com'>
        <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center p-4">
        {this.state.showAlert && this.state.alertProps ? 
        <div className='fixed top-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-sm px-4'>
            <div 
                className="rounded-xl shadow-2xl border-l-4 p-4 flex items-center justify-between"
                style={{
                    backgroundColor: this.state.alertProps.bgColorTailwind?.includes('red') ? '#fef2f2' : '#f0fdf4',
                    borderLeftColor: this.state.alertProps.bgColorTailwind?.includes('red') ? '#dc2626' : '#16a34a',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                }}
            >
                <div className="flex items-center">
                    <div 
                        className="flex-shrink-0 w-5 h-5 mr-3 rounded-full flex items-center justify-center"
                        style={{
                            backgroundColor: this.state.alertProps.bgColorTailwind?.includes('red') ? '#dc2626' : '#16a34a',
                        }}
                    >
                        <span className="text-white text-xs font-bold">
                            {this.state.alertProps.bgColorTailwind?.includes('red') ? '!' : '✓'}
                        </span>
                    </div>
                    <p 
                        className="text-sm font-medium"
                        style={{
                            color: this.state.alertProps.bgColorTailwind?.includes('red') ? '#dc2626' : '#16a34a',
                        }}
                    >
                        {this.state.alertProps.stringToShow}
                    </p>
                </div>
                <button
                    onClick={this.state.alertProps.onCloseOrNo}
                    className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600 transition-colors duration-150"
                >
                    <span className="sr-only">Close</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>
        </div> : null}
        
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            {/* Header Section */}
            <div className="bg-white px-8 pt-8 pb-6 text-center border-b border-gray-100">
                <div className="flex justify-center mb-4">
                    {ClaraCallLogo}
                </div>
                <div className="space-y-1">
                    <h1 className="text-2xl font-bold text-gray-900">
                        {this.state.authState === AuthState.SIGNIN ? 'Welcome back' : 
                         this.state.authState === AuthState.SIGNUP ? 'Create your account' :
                         this.state.authState === AuthState.PASSWORD_RESET ? 'Reset password' : 'Reset your password'}
                    </h1>
                    <p className="text-gray-600 text-sm">
                        {this.state.authState === AuthState.SIGNIN ? 'Sign in to your ClaraCall account' :
                         this.state.authState === AuthState.SIGNUP ? 'Get started with ClaraCall today' :
                         this.state.authState === AuthState.PASSWORD_RESET ? 'Enter your new password' : 'We\'ll send you a reset link'}
                    </p>
                </div>
            </div>

            {/* Invitation Banner */}
            {this.renderInvitationBanner()}

            {/* Main Content */}
            <div className="px-8 py-6">
                {[AuthState.SIGNIN, AuthState.SIGNUP].includes(this.state.authState) ? this.renderLoginOrSignupPage() : 
                this.state.authState === AuthState.PASSWORD_RESET_REQUEST ? 
                this.renderPasswordResetRequest() : this.state.authState === AuthState.PASSWORD_RESET ? this.renderPasswordReset(): null}
            </div>
        </div>
    </div>
    </GoogleOAuthProvider>
    

    )
  }
}

  const mapStateToProps = (state: RootState) => {
    return {};
  };
  
  const ReduxWrapped = connect(mapStateToProps)(AuthenticationImpl)
  
  export { ReduxWrapped as Authentication}
